import React, { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useAdminSeries } from '../../../hooks/useAdminSeries';
import type { ResourceSeries, ResourceSermon } from '../../../hooks/useAdminSeries';
import { SeriesManagerList } from './SeriesManagerList';
import { SeriesEditorModal } from './SeriesEditorModal';
import { SermonEditorModal } from './SermonEditorModal';
import { supabase } from '../../../lib/supabase';

export function SeriesManagerTab() {
  const {
    series, loading, error,
    createSeries, updateSeries, deleteSeries, togglePublished, uploadCoverArt,
    createResourceSermon, updateResourceSermon, deleteResourceSermon, uploadAsset,
  } = useAdminSeries();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSeries, setEditingSeries] = useState<ResourceSeries | null>(null);
  const [sermonSeries, setSermonSeries] = useState<ResourceSeries | null>(null);
  const [sermons, setSermons] = useState<ResourceSermon[]>([]);
  const [sermonsLoading, setSermonsLoading] = useState(false);

  const openEditor = (s: ResourceSeries | null) => {
    setEditingSeries(s);
    setEditorOpen(true);
  };

  const openSermonManager = async (s: ResourceSeries) => {
    setSermonSeries(s);
    setSermonsLoading(true);
    const { data } = await supabase
      .from('resource_sermons')
      .select('*')
      .eq('series_id', s.id)
      .order('sermon_order');
    setSermons((data as ResourceSermon[]) ?? []);
    setSermonsLoading(false);
  };

  const handleSave = async (fields: Partial<ResourceSeries>) => {
    if (editingSeries) {
      await updateSeries(editingSeries.id, fields);
    } else {
      return await createSeries(fields);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-midnight dark:text-soft-white">Sermon Series Library</h3>
          <p className="text-xs text-slate dark:text-soft-white/50 mt-0.5">
            Manage pre-packaged series available to Series Access subscribers.
          </p>
        </div>
        <button
          onClick={() => openEditor(null)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-electric text-white text-sm rounded-lg hover:bg-electric/90"
        >
          <Plus className="h-4 w-4" />
          New Series
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-slate" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>
      )}

      {!loading && (
        <SeriesManagerList
          series={series}
          onEdit={openEditor}
          onManageSermons={openSermonManager}
          onTogglePublished={togglePublished}
          onDelete={deleteSeries}
        />
      )}

      {editorOpen && (
        <SeriesEditorModal
          series={editingSeries}
          onSave={handleSave}
          onUploadCoverArt={uploadCoverArt}
          onClose={() => { setEditorOpen(false); setEditingSeries(null); }}
        />
      )}

      {sermonSeries && !sermonsLoading && (
        <SermonEditorModal
          series={sermonSeries}
          sermons={sermons}
          onCreateSermon={createResourceSermon}
          onUpdateSermon={updateResourceSermon}
          onDeleteSermon={deleteResourceSermon}
          onUploadAsset={uploadAsset}
          onClose={() => setSermonSeries(null)}
        />
      )}
    </div>
  );
}
